// ---------------------------------------------------------------------------
// Filter State to Chips Converter
// ---------------------------------------------------------------------------
// Converts Discovery filter state to display chips for the toolbar.
// Pure function - derives chips directly from filter state (no separate chip state).
// ---------------------------------------------------------------------------

import type { DiscoveryFilter } from './types';
import type { FilterChip } from '@/components/v11/discovery/types';

/**
 * Convert filter state to display chips for the toolbar.
 * Pure function -- derives chips directly from filter state.
 *
 * Each filter value becomes one chip with a unique ID for removal.
 * Chip ID format: "{dimension}-{value}" (e.g., "gender-2", "verified-yes")
 *
 * This format MUST match what removeFilter() expects in useDiscoveryUrlFilters.
 *
 * @param filters - Array of active filters
 * @returns Array of filter chips for display
 *
 * @example
 * const filters = [
 *   { dimension: 'gender', dimensionLabel: 'Gender', values: [2], valueLabels: ['female'] }
 * ]
 * filterStateToChips(filters)
 * // => [{ id: 'gender-2', label: 'Gender', value: 'female', category: 'gender' }]
 */
export function filterStateToChips(filters: readonly DiscoveryFilter[]): readonly FilterChip[] {
  const chips: FilterChip[] = [];

  for (const filter of filters) {
    const { dimension, dimensionLabel, values, valueLabels } = filter;

    const isDimensionEnabled = filter.enabled !== false;
    const disabledValues = filter.disabledValues;

    // Sentiment range: single chip with range display
    if (dimension === 'sentiment' && values.length === 2) {
      chips.push({
        id: `${dimension}-range`,
        label: dimensionLabel,
        value: `${values[0]}-${values[1]}`,
        category: dimension,
        enabled: isDimensionEnabled,
      });
      continue;
    }

    // Standard: one chip per value (per-value enabled state)
    for (let i = 0; i < values.length; i++) {
      const valueStr = String(values[i]);
      const isValueDisabled = disabledValues?.has(valueStr) ?? false;
      const platform = filter.valuePlatforms?.[i];
      chips.push({
        id: `${dimension}-${values[i]}`,
        label: dimensionLabel,
        value: valueLabels[i] || valueStr,
        category: dimension,
        enabled: isDimensionEnabled && !isValueDisabled,
        ...(platform ? { platform } : {}),
      });
    }
  }

  return chips;
}
